import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import './authForm.css';

const AuthForm = () => {
    // view: 'login' | 'forgotPassword' | 'resetPassword'. No 'register' — accounts
    // are created by an admin on the Team page, not self-served here.
    const [view, setView] = useState('login');
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        resetToken: '',
        newPassword: ''
    });
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);

    const { login, forgotPassword, resetPassword, sessionExpired } = useAuth();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccessMessage('');

        let result;

        switch (view) {
            case 'login':
                result = await login(formData.email, formData.password, rememberMe);
                break;
            case 'forgotPassword':
                result = await forgotPassword(formData.email);
                if (result.success) {
                    // The code arrives by email and nowhere else, so move straight to the
                    // reset screen with the field empty for them to paste into. It used to
                    // be pre-filled from the response, which meant requesting a reset for
                    // someone else's address handed you their account.
                    setSuccessMessage('If an account with that email exists, a reset code has been emailed to it. Paste the code below to set a new password.');
                    setView('resetPassword');
                }
                break;
            case 'resetPassword':
                result = await resetPassword(formData.email, formData.resetToken, formData.newPassword);
                if (result.success) {
                    setSuccessMessage('Password reset successfully. You can now log in.');
                    setFormData({ username: '', email: formData.email, password: '', resetToken: '', newPassword: '' });
                    setView('login');
                }
                break;
            default:
                break;
        }

        if (result && !result.success) {
            setError(result.error);
        }

        setLoading(false);
    };

    const switchView = (newView) => {
        setError('');
        setSuccessMessage('');
        setView(newView);
    };

    const getTitle = () => {
        switch (view) {
            case 'login': return 'Login';
            case 'forgotPassword': return 'Forgot Password';
            case 'resetPassword': return 'Reset Password';
            default: return '';
        }
    };

    return (
        <div className="artemis-auth">
            <div className="artemis-auth-card">
                <div className="artemis-auth-brand">
                    <img src="/artemis-gold.png" alt="Artemis" className="artemis-auth-logo" />
                    <p className="artemis-auth-tagline">Roofing CRM</p>
                </div>

                <h2 className="artemis-auth-heading">{getTitle()}</h2>

                {/* Shown when the session lapsed rather than when someone signed out
                    on purpose, so "why am I looking at a login screen" has an answer
                    on the screen instead of being worked out by trial and error. */}
                {sessionExpired && !error && view === 'login' && (
                    <div className="artemis-auth-alert error">
                        Your session expired. Please sign in again.
                    </div>
                )}

                {error && (
                    <div className="artemis-auth-alert error">{error}</div>
                )}

                {successMessage && (
                    <div className="artemis-auth-alert success">{successMessage}</div>
                )}

                <form onSubmit={handleSubmit}>
                    {(view === "login" || view === "forgotPassword") && (
                        <div className="artemis-auth-field">
                            <label>Email</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    )}

                    {view === "login" && (
                        <div className="artemis-auth-field">
                            <label>Password</label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    )}

                    {view === 'resetPassword' && (
                        <>
                            <div className="artemis-auth-field">
                                <label>Reset Code</label>
                                <input
                                    type="text"
                                    name="resetToken"
                                    value={formData.resetToken}
                                    onChange={handleChange}
                                    placeholder="Paste the code from your email"
                                    required
                                />
                            </div>
                            <div className="artemis-auth-field">
                                <label>New Password</label>
                                <input
                                    type="password"
                                    name="newPassword"
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                    required
                                    minLength={6}
                                />
                            </div>
                        </>
                    )}

                    {view === 'login' && (
                        <label className="artemis-auth-remember">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            <span>Remember me</span>
                        </label>
                    )}

                    <button type="submit" disabled={loading} className="artemis-auth-btn">
                        {loading ? 'Please wait…' : getTitle()}
                    </button>
                </form>

                <div className="artemis-auth-links">
                    {view === 'login' && (
                        <>
                            <p>
                                <button type="button" onClick={() => switchView('forgotPassword')} className="artemis-auth-link">
                                    Forgot your password?
                                </button>
                            </p>
                            {/* No self-registration. Accounts are created by an admin on
                                the Team page — the endpoint requires Admin now, so a
                                form here could only ever fail. Says who to ask instead
                                of leaving a dead end. */}
                            <p className="artemis-auth-note">
                                Need an account? Ask an admin to add you.
                            </p>
                        </>
                    )}

                    {(view === 'forgotPassword' || view === 'resetPassword') && (
                        <p>
                            <button type="button" onClick={() => switchView('login')} className="artemis-auth-link">
                                Back to Login
                            </button>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuthForm;
